import { useParams } from "react-router-dom";
import { useAuthHook } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import PropertyDataForm from "@/components/propertyDataForm";
import ValuationSidebar from "@/components/valuation-sidebar";
import ValuationSidebarSuperVisor from "@/components/supervisor/ValuationResultsSupervisor";
import { CurrencyProvider } from "@/context/CurrencyContext";

export default function PropertyValuation() {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuthHook();

  // Add state for property data
  const [property, setProperty] = useState<any>(null);
  const [loadingProperty, setLoadingProperty] = useState(false);
  const apiUrl =
    import.meta.env.VITE_API_BASE_URL ||
    "https://valuate-api.vercel.app/api/v1";

  // Fetch property data if we have an ID (editing mode)
  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) {
        // New valuation - no property to fetch
        setProperty(null);
        return;
      }

      setLoadingProperty(true);
      try {
        const token = localStorage.getItem("valuation_token");
        const response = await fetch(`${apiUrl}/properties/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          setProperty(result.data);
        } else {
          toast({
            title: "Error",
            description: "Failed to fetch property data",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching property:", error);
        toast({
          title: "Error",
          description: "Failed to load property",
          variant: "destructive",
        });
      } finally {
        setLoadingProperty(false);
      }
    };

    fetchProperty();
  }, [id, toast]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  if (isLoading || loadingProperty) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Determine mode based on user role
  const userRole = user?.role || "collector";
  const mode = userRole === "supervisor" ? "supervisor" : "collector";

  return (
    <CurrencyProvider>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {id ? "Edit Property" : "New"} Valuation
            </h2>
            <p className="text-gray-600">
              {id
                ? "Update property information and valuation data"
                : "Create a property valuation record"}
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Property Data Collection */}
            <div className="lg:col-span-2">
              <PropertyDataForm isForm={false} property={property} />
            </div>

            {/* Valuation Sidebar */}
            <div className="lg:col-span-1">
              {mode === "collector" ? (
                <ValuationSidebar
                  property={property}
                  propertyId={id}
                  mode={mode}
                  onValuationComplete={() => {
                    // Refresh property data or show success message
                    toast({
                      title: "Success",
                      description: "Valuation completed successfully",
                      variant: "default",
                    });

                    // If editing, refresh the page to show updated data
                    if (id) {
                      setTimeout(() => {
                        window.location.reload();
                      }, 1500);
                    }
                  }}
                />
              ) : (
                <ValuationSidebarSuperVisor />
              )}
            </div>
          </div>
        </div>
      </div>
    </CurrencyProvider>
  );
}